<template>
  <a-row :gutter="24">
    <a-col :md="24">
      <a-card :style="cardStyle" :bordered="false">
        <div class="section-title">小程序配置</div>
        <div class="section-tip">配置用于独立商户打包小程序</div>
        <a-form class="setting-form">
          <a-form-item label="小程序名称" :labelCol="formLabelCol" :wrapperCol="formWrapperCol" required>
            <a-input v-model="form.name" placeholder="请输入小程序名称" style="width: 400px" />
          </a-form-item>
          <a-form-item label="原始ID" :labelCol="formLabelCol" :wrapperCol="formWrapperCol" required>
            <a-input v-model="form.originalAppId" placeholder="请输入小程序原始ID" style="width: 400px" />
            <div class="field-tip">获取方式：微信公众平台-设置-账号信息-原始ID</div>
          </a-form-item>
          <a-form-item label="AppID(小程序ID)" :labelCol="formLabelCol" :wrapperCol="formWrapperCol" required>
            <a-input v-model="form.appId" placeholder="请输入小程序AppID" style="width: 400px" />
            <div class="field-tip">获取方式：微信公众平台-开发-开发设置-开发者ID-小程序AppID</div>
          </a-form-item>
          <a-form-item label="AppSecret(小程序密钥)" :labelCol="formLabelCol" :wrapperCol="formWrapperCol" required>
            <!-- 密码框回显脱敏掩码（契约：GET 返回掩码+configured 标志；PUT 原样传掩码则后端不更新该字段） -->
            <a-input v-model="form.appSecret" :type="secretType" placeholder="请输入小程序AppSecret" style="width: 400px">
              <a-icon slot="suffix" :type="secretVisible ? 'eye' : 'eye-invisible'" @click="secretVisible = !secretVisible" />
            </a-input>
            <div class="field-tip">获取方式：微信公众平台-开发-开发设置-开发者ID-小程序AppSecret</div>
          </a-form-item>
          <a-form-item label="代码上传秘钥" :labelCol="formLabelCol" :wrapperCol="formWrapperCol">
            <a-textarea v-model="form.privKey" placeholder="请输入privKey" :rows="4" style="width: 400px" />
            <div class="field-tip">获取方式：微信公众平台-开发-开发设置-小程序代码上传-小程序代码上传密钥,并关闭ip白名单</div>
          </a-form-item>
          <a-form-item label="扫码直达域名" :labelCol="formLabelCol" :wrapperCol="formWrapperCol">
            <a-input v-model="form.scanDomain" placeholder="如：https://scancode.example.com（已备案）" style="width: 400px" />
            <div class="field-tip">配置后桌码二维码生成为链接码，顾客微信扫码直达小程序点餐页（需在微信公众平台"扫普通链接二维码打开小程序"中添加规则，测试链接可绑体验版）</div>
          </a-form-item>
          <a-form-item label="小程序码打开版本" :labelCol="formLabelCol" :wrapperCol="formWrapperCol">
            <a-select v-model="form.wxacodeEnv" style="width: 200px">
              <a-select-option value="trial">体验版（未发布小程序）</a-select-option>
              <a-select-option value="release">正式版（已发布）</a-select-option>
            </a-select>
            <div class="field-tip">生成桌台小程序码（菊花码）时打开的版本；小程序发布后请切换为正式版</div>
          </a-form-item>
        </a-form>
        <div class="form-footer">
          <a-button type="primary" :loading="saving" @click="handleSave">保存</a-button>
        </div>
      </a-card>
    </a-col>
  </a-row>
</template>

<script>
import { getAction, putAction } from '@/api/manage'
import { mixinDevice } from '@/utils/mixin'

/**
 * 商户小程序配置页（租户级，克隆云贝小程序配置基础设置：name/appId/appSecret/originalAppId/privKey）
 * API 契约（Task 2A 另一代理实现中）：GET/PUT /api/plugin/dining/store-setting/wxapp
 *   - GET 回显脱敏掩码 + configured 标志
 *   - PUT 传掩码原样则后端不更新该字段（用户无需重输已配置的密钥）
 * 存储：jsh_dining_store_set（set_type='wxapp'，租户级 store_id=0），AppSecret/privKey 加密存储
 * 与微信支付商户号配置（jsh_pay_merchant）相互独立：本页为小程序本身身份配置
 * 菜单挂载：菜单 SQL 后续统一出（url 建议 /plugin/dining/wxapp）
 */
const SETTING_PREFIX = '/api/plugin/dining/store-setting'

const WXAPP_DEFAULTS = {
  name: '',
  appId: '',
  appSecret: '',
  originalAppId: '',
  privKey: '',
  scanDomain: '',
  wxacodeEnv: 'trial'
}

export default {
  name: 'DiningWxappSetting',
  mixins: [mixinDevice],
  data() {
    return {
      formLabelCol: { span: 5 },
      formWrapperCol: { span: 18, offset: 1 },
      form: Object.assign({}, WXAPP_DEFAULTS),
      secretVisible: false,
      saving: false,
      cardStyle: ''
    }
  },
  computed: {
    secretType() {
      return this.secretVisible ? 'text' : 'password'
    }
  },
  created() {
    if (this.isDesktop()) {
      this.cardStyle = 'height:' + (document.documentElement.clientHeight - 100) + 'px'
    }
    this.loadConfig()
  },
  methods: {
    loadConfig() {
      const that = this
      getAction(`${SETTING_PREFIX}/wxapp`).then(res => {
        if (res.code === 200) {
          // appSecret/privKey 为后端下发的脱敏掩码，原样回显；保存时原样传回则后端不更新
          that.form = Object.assign({}, WXAPP_DEFAULTS, res.data || {})
        } else {
          that.$message.warning(res.message)
        }
      })
    },
    handleSave() {
      const that = this
      if (!this.form.name || !String(this.form.name).trim()) {
        this.$message.warning('请输入小程序名称')
        return
      }
      if (!this.form.appId || !String(this.form.appId).trim()) {
        this.$message.warning('请输入小程序AppID')
        return
      }
      if (!this.form.originalAppId || !String(this.form.originalAppId).trim()) {
        this.$message.warning('请输入小程序原始ID')
        return
      }
      if (!this.form.appSecret || !String(this.form.appSecret).trim()) {
        this.$message.warning('请输入小程序AppSecret')
        return
      }
      // 扫码直达域名选填：填了必须 https:// 开头（微信"扫普通链接二维码"要求已备案 https 域名）
      const domain = String(this.form.scanDomain || '').trim()
      if (domain && !/^https:\/\//.test(domain)) {
        this.$message.warning('扫码直达域名必须以 https:// 开头')
        return
      }
      this.saving = true
      putAction(`${SETTING_PREFIX}/wxapp`, this.form).then(res => {
        if (res.code === 200) {
          that.$message.success('保存成功')
          that.loadConfig()
        } else {
          that.$message.warning(res.message)
        }
      }).finally(() => {
        that.saving = false
      })
    }
  }
}
</script>

<style scoped>
@import '~@assets/less/common.less';
.setting-form {
  max-width: 760px;
}
.field-tip {
  color: #999;
  font-size: 12px;
  line-height: 16px;
  margin-top: 4px;
}
.section-title {
  font-size: 15px;
  font-weight: bold;
  padding-left: 10px;
  border-left: 3px solid #1890ff;
  margin: 8px 0 6px;
}
.section-tip {
  color: #999;
  font-size: 12px;
  margin-bottom: 16px;
}
.form-footer {
  text-align: center;
  margin-top: 16px;
  border-top: 1px solid #e8e8e8;
  padding-top: 16px;
  max-width: 760px;
}
</style>
